import {
  TEntityResponse,
  TEntityListResponse,
  TCreateUserReqBody,
  TUserResBody,
  TUserSummaryResBody,
  TUpdateUserReqBody,
  TMeUserResBody,
} from '@poseidon-city/shared';

import { TAPIClient } from '../client';

type TUsersClient = {
  create(body: TCreateUserReqBody): Promise<TEntityResponse<'user', TUserResBody> | undefined>;
  update(id: string, body: TUpdateUserReqBody): Promise<TEntityResponse<'user', TUserResBody> | undefined>;
  delete(id: string): Promise<void>;
  get(id: string): Promise<TEntityResponse<'user', TUserResBody> | undefined>;
  list(limit: number, page: number): Promise<TEntityListResponse<'user', TUserSummaryResBody> | undefined>;
  list_company_users(
    limit: number,
    page: number,
    companyId: string
  ): Promise<TEntityListResponse<'user', TUserSummaryResBody> | undefined>;
  me(): Promise<TEntityResponse<'user', TMeUserResBody> | undefined>;
  permissions(): Promise<TEntityResponse<'permission', any> | undefined>;
};

function UsersClient(apiClient: TAPIClient): TUsersClient {
  return {
    async create(body: TCreateUserReqBody): Promise<TEntityResponse<'user', TUserResBody> | undefined> {
      const data = await apiClient.post<TCreateUserReqBody, TEntityResponse<'user', TUserResBody>>('/users', body);

      if (data && data.type !== 'user') {
        throw new Error('Invalid entity type received, expected: user, received: ' + data.type);
      }

      return data;
    },

    async update(id: string, body: TUpdateUserReqBody): Promise<TEntityResponse<'user', TUserResBody> | undefined> {
      const data = await apiClient.put<TUpdateUserReqBody, TEntityResponse<'user', TUserResBody>>(`/users/${id}`, body);

      if (data && data.type !== 'user') {
        throw new Error('Invalid entity type received, expected: user, received: ' + data.type);
      }

      return data;
    },

    async delete(id: string): Promise<void> {
      return await apiClient.delete(`/users/${id}`);
    },

    async get(id: string): Promise<TEntityResponse<'user', TUserResBody> | undefined> {
      const data = await apiClient.get<TEntityResponse<'user', TUserResBody>>(`/users/${id}`);

      if (data && data.type !== 'user') {
        throw new Error('Invalid entity type received, expected: user, received: ' + data.type);
      }

      return data;
    },

    async list(limit: number, page: number): Promise<TEntityListResponse<'user', TUserSummaryResBody> | undefined> {
      const data = await apiClient.get<TEntityListResponse<'user', TUserSummaryResBody>>(
        `/users?limit=${limit}&page=${page}`
      );

      if (data && data.type !== 'user') {
        throw new Error('Invalid entity type received, expected: user, received: ' + data.type);
      }

      return data;
    },

    async list_company_users(
      limit: number,
      page: number,
      companyId: string
    ): Promise<TEntityListResponse<'user', TUserSummaryResBody> | undefined> {
      const data = await apiClient.get<TEntityListResponse<'user', TUserSummaryResBody>>(
        `/users?limit=${limit}&page=${page}&filter[company_id]=${companyId}`
      );

      if (data && data.type !== 'user') {
        throw new Error('Invalid entity type received, expected: user, received: ' + data.type);
      }

      return data;
    },

    async me(): Promise<TEntityResponse<'user', TMeUserResBody> | undefined> {
      const data = await apiClient.get<TEntityResponse<'user', TMeUserResBody>>('/me');

      if (data && data.type !== 'user') {
        throw new Error('Invalid entity type received, expected: user, received: ' + data.type);
      }

      return data;
    },

    async permissions(): Promise<TEntityResponse<'permission', any> | undefined> {
      const data = await apiClient.get<TEntityResponse<'permission', any>>('/me/permissions ');

      if (data && data.type !== 'permission') {
        throw new Error('Invalid entity type received, expected: permission, received: ' + data.type);
      }

      return data;
    },
  };
}

export { TUsersClient, UsersClient };
